import { UpgradeLink } from "@/components/common/UpgradeLink/UpgradeLink"
import { InviteMemberDialog } from "@/components/feature/TeamManagement/InviteMemberDialog"
import { RemoveMemberDialog } from "@/components/feature/TeamManagement/RemoveMemberDialog"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import { useBillingStatus } from "@/services/api/billingService"
import { useGetTeam } from "@/services/api/teamService"
import type { SeatUsage, TeamMember } from "@/types/team"
import { URLS } from "@/utils/urls.const"
import { userDisplayName, userInitials } from "@/utils/userIdentity"
import { UserPlus } from "lucide-react"
import { useState } from "react"
import {
  SettingsList,
  SettingsListGroup,
  SettingsListRow,
} from "../../components/SettingsList"
import { SettingsPage, SettingsSection } from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"
import { MemberAccessSheet } from "./MemberAccessSheet"

function SeatsSection({ seats }: { seats: SeatUsage }) {
  const { translate } = useTranslation()
  const { data: billing } = useBillingStatus()
  const full = seats.used >= seats.included
  // A paying team that outgrew its plan talks to sales; a trial upgrades itself.
  // While the status loads, the upgrade button is the safe default.
  const paying = billing?.hasActiveSubscription === true
  return (
    <SettingsSection
      title={translate("SETTINGS/MEMBERS/SEATS_TITLE")}
      description={
        full
          ? translate("TEAM/SEAT_LIMIT_MESSAGE", { included: seats.included })
          : undefined
      }
      footer={
        full && (
          <Button variant="outline" asChild>
            {paying ? (
              <a href={URLS.ENTERPRISE_CONTACT}>
                {translate("TEAM/CONTACT_US")}
              </a>
            ) : (
              <UpgradeLink>{translate("FREE_TRIAL/UPGRADE")}</UpgradeLink>
            )}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <Typography as="small">
            {translate("TEAM/SEATS_USED", {
              used: seats.used,
              included: seats.included,
            })}
          </Typography>
          {seats.plan && <Typography as="muted">{seats.plan}</Typography>}
        </div>
        <Progress
          value={seats.included > 0 ? (seats.used / seats.included) * 100 : 0}
          className={cn(full && "[&>div]:bg-destructive")}
        />
      </div>
    </SettingsSection>
  )
}

function MemberRow({
  member,
  onOpen,
}: {
  member: TeamMember
  /** Admins open a member's access; everyone else just sees the list. */
  onOpen?: () => void
}) {
  const { translate } = useTranslation()
  const admin = member.role === "admin"
  return (
    <SettingsListRow
      label={member.email}
      leading={
        <Avatar className="size-9">
          <AvatarFallback className="bg-primary/10 text-primary">
            <Typography as="small">
              {userInitials(userDisplayName({ email: member.email }))}
            </Typography>
          </AvatarFallback>
        </Avatar>
      }
      badges={
        member.isSelf && (
          <Badge variant="outline">{translate("TEAM/YOU")}</Badge>
        )
      }
      description={
        <Typography as="muted">
          {translate(
            member.status === "pending"
              ? "TEAM/STATUS_PENDING"
              : "TEAM/STATUS_ACTIVE",
          )}
        </Typography>
      }
      trailing={
        <Badge variant={admin ? "default" : "outline"}>
          {translate(admin ? "TEAM/ROLE_ADMIN" : "TEAM/ROLE_MEMBER")}
        </Badge>
      }
      onClick={onOpen}
    />
  )
}

export function MembersPage() {
  const { translate } = useTranslation()
  const { isAdmin } = useUser()
  const { data, isLoading, isError, isFetching, refetch } = useGetTeam()
  const [inviting, setInviting] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [removing, setRemoving] = useState<TeamMember | null>(null)

  const members = data?.members ?? []
  // Gone from the list (revoked, removed) closes the panel.
  const openMember = members.find((member) => member.id === openId) ?? null
  const seatsFull = !!data && data.seats.used >= data.seats.included

  return (
    <SettingsPage
      title={translate("SETTINGS/MEMBERS/TITLE")}
      description={translate("SETTINGS/MEMBERS/DESCRIPTION")}
      notice={isAdmin ? undefined : translate("TEAM/MEMBER_ONLY_NOTE")}
      action={
        isAdmin && (
          <Button
            onClick={() => setInviting(true)}
            disabled={!data || seatsFull}
            title={seatsFull ? translate("TEAM/SEAT_LIMIT_TITLE") : undefined}
          >
            <UserPlus />
            {translate("TEAM/INVITE_BUTTON")}
          </Button>
        )
      }
    >
      {!data ? (
        <SettingsSection>
          {isLoading && !isError ? (
            <SettingsSkeleton />
          ) : (
            <SettingsLoadError
              pending={isFetching}
              onRetry={() => void refetch()}
            />
          )}
        </SettingsSection>
      ) : (
        <>
          <SeatsSection seats={data.seats} />
          <SettingsSection title={translate("SETTINGS/MEMBERS/LIST_TITLE")}>
            {members.length === 0 ? (
              <Typography as="muted">{translate("TEAM/EMPTY")}</Typography>
            ) : (
              <SettingsList>
                <SettingsListGroup>
                  {members.map((member) => (
                    <MemberRow
                      key={member.id}
                      member={member}
                      onOpen={isAdmin ? () => setOpenId(member.id) : undefined}
                    />
                  ))}
                </SettingsListGroup>
              </SettingsList>
            )}
          </SettingsSection>
        </>
      )}

      <MemberAccessSheet
        member={openMember}
        onClose={() => setOpenId(null)}
        onRequestRemove={setRemoving}
      />
      <InviteMemberDialog open={inviting} onOpenChange={setInviting} />
      <RemoveMemberDialog member={removing} onClose={() => setRemoving(null)} />
    </SettingsPage>
  )
}
